import { type BulkPropertyRow } from "../types";
import { type AcquirePolicy } from "./acquirePolicy";
import {
  ALL_MATTERS_FIELDS,
  CHECKBOX_FIELDS,
  isAllMattersField,
} from "./checkboxFields";
import { pictureTypeOfField } from "./reuseAvailability";
import { selectPictureSource, type PictureSelection } from "./reuseSelection";
import { toZenbujikoOptions } from "./zenbujikoConstruction";

export type SourceKind = PictureSelection["kind"];

/** 取得方法セルの1行（書類ごと） */
export interface SourceLine {
  kind: SourceKind;
  text: string;
}

// フッター内訳・確認ダイアログと同じ語
export const SOURCE_LABEL: Record<SourceKind, string> = {
  new: "新規取得",
  acquired: "取得済",
  reuse: "リユース",
  excluded: "対象外",
};

const PICTURE_LABEL: Record<string, string> = Object.fromEntries(
  CHECKBOX_FIELDS.filter(({ field }) => !isAllMattersField(field)).map(
    ({ field, headerName }) => [field, headerName]
  )
);

/**
 * 行のチェック済み書類が、今の取得条件で送信時にどう振り分けられるかを書類ごとに1行でまとめる。
 * 送信・フッター内訳と同じ判定（selectPictureSource）を使う。
 * 取得済・リユースは候補の取得日を添える。並びは列順（全部事項が先頭）
 *
 * @example
 * summarizeRowSources(row, ["kyoutan", "ownerInfo"], policy, 30, now)
 * // [
 * //   { kind: "new", text: "全部事項: 新規取得" },
 * //   { kind: "acquired", text: "所有者事項: 取得済 2025-03-03" },
 * // ]
 */
export const summarizeRowSources = (
  row: BulkPropertyRow,
  checkedFields: readonly string[],
  policy: AcquirePolicy,
  pastDays: number,
  now: Date
): SourceLine[] => {
  const lines: SourceLine[] = [];
  const push = (label: string, selected: PictureSelection): void => {
    const date =
      selected.kind === "acquired" || selected.kind === "reuse"
        ? selected.candidate.latestReceivedDate
        : null;
    lines.push({
      kind: selected.kind,
      text: `${label}: ${SOURCE_LABEL[selected.kind]}${
        date == null ? "" : ` ${date}`
      }`,
    });
  };

  const zenbujikoFields = ALL_MATTERS_FIELDS.filter((field) =>
    checkedFields.includes(field)
  );
  if (zenbujikoFields.length > 0) {
    push(
      "全部事項",
      selectPictureSource(
        row,
        "ZENBUJIKO",
        toZenbujikoOptions(zenbujikoFields),
        policy,
        pastDays,
        now
      )
    );
  }
  for (const { field } of CHECKBOX_FIELDS) {
    const pictureType = pictureTypeOfField(field);
    if (
      isAllMattersField(field) ||
      pictureType == null ||
      !checkedFields.includes(field)
    ) {
      continue;
    }
    push(
      PICTURE_LABEL[field],
      selectPictureSource(row, pictureType, [], policy, pastDays, now)
    );
  }

  return lines;
};

/** 取得方法セルの行数（行高の計算に使う。未チェックの行は 1） */
export const countSourceLines = (
  row: BulkPropertyRow,
  checkedFields: readonly string[],
  policy: AcquirePolicy,
  pastDays: number,
  now: Date
): number =>
  Math.max(
    1,
    summarizeRowSources(row, checkedFields, policy, pastDays, now).length
  );
